import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AppBar, Button, HeroBanner, SectionHeader } from 'components/ds';
import { BottomSheet } from 'components/common/bottomSheet/BottomSheet';
import { useAlert } from 'contexts/AlertContext';
import { openShare, copyToClipboard } from 'common/utils/share';
import { meetingStore } from 'store/meetingStore';
import { MeetingInviteSection } from 'components/meeting/MeetingInviteSection';
import { InviteFromPastMeetings } from 'components/meeting/InviteFromPastMeetings';
import { SubPlaceModal, SubPlace, SubPlaceForm, getCategoryLabel } from 'components/meeting/SubPlaceModal';
import { isOkResponse, errorMessageOf } from 'common/utils/apiResponse';
import { LastChangeRow, ChangeHistorySheet } from 'components/meeting/ChangeHistory';
import { MapContainer } from 'components/map';
import { ExternalMapSheet } from 'components/map/ExternalMapSheet';
import { parseTimeToMinutes, toLocalISOString } from 'common/utils/datetime';
import { useUserInfo } from 'hooks/useAuth';
import { useNowSpot, type NowSpot } from './hooks/useNowSpot';
import { JournalSummaryCard } from 'components/journal/JournalSummaryCard';
import { useJournalSummary } from 'hooks/useMeetingJournal';
import { TraceLiveCard } from 'components/journal/TraceLiveCard';
import { TraceHistorySection } from 'components/journal/TraceHistorySection';
import { CourseRegisterSection } from 'components/course/CourseRegisterSection';
import { CourseBrowseSection } from 'components/course/CourseBrowseSection';
import { TraceInsightSection } from 'components/journal/TraceInsightSection';
import { useMeetingTrace } from 'hooks/useMeetingTrace';
import { useTraceInsights } from 'hooks/useTraceInsights';
import { useTraceLive } from 'hooks/useTraceLive';
import LadderGameModal, { LadderParticipant } from 'components/common/LadderGameModal';
import { MeetingRepository } from 'repository/meetingRepository';
import { usePullToRefresh } from 'hooks/usePullToRefresh';
import useRealtimeMemberLocations, { MemberLocationMap } from 'hooks/useRealtimeMemberLocations';
import { haversineMeters } from 'common/utils/geo';
import {
    IcHotel, IcPin, IcPlus, IcUsers, IcChat, IcDice, IcClock, IcClose,
    IcCrown, IcCamera, IcChevronRight, IcEdit, IcTrash,
} from 'components/common/icons/EmojiIcons';
import { useTripView, AttendanceStatus, DayView, MemberView, DestinationInfo, PlaceItem, HotelInfo } from './hooks/useTripView';
import { usePlaceReviews, feedPhotos, placePhotosPath } from 'hooks/usePlaceReviews';
import styles from './MeetingDetailV6.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

// ═══════════════════════ 유틸 ═══════════════════════

const initialsOf = (name: string): string => {
    if (!name) return '?';
    const trimmed = name.trim();
    return trimmed.length > 0 ? trimmed.charAt(0) : '?';
};

const parseISODate = (iso: string): Date | null => {
    if (!iso) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
};

/** 요일 로케일 포맷 — "목" / "Thu" / "木" */
const formatWeekday = (date: Date, locale: string): string => {
    try {
        return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
    } catch {
        return '';
    }
};

/** "MM/DD" */
const formatMonthDay = (date: Date): string => {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${m}/${d}`;
};

/** place.time("HH:mm") + dayDateISO → epoch ms (place time 없으면 null) */
const parsePlaceStartMs = (place: PlaceItem, dayDateISO: string): number | null => {
    if (!place.time) return null;
    const m = place.time.match(/^(\d{1,2}):(\d{2})/);
    if (!m) return null;
    const dayDate = parseISODate(dayDateISO);
    if (!dayDate) return null;
    const dt = new Date(dayDate);
    dt.setHours(Number(m[1]), Number(m[2]), 0, 0);
    return dt.getTime();
};

type PlaceStatus = 'past' | 'current' | 'future';

/** 현재 진행 중인 place idx (없으면 -1) — 지난 시간의 마지막 place 를 current 로 취급 */
const computeCurrentPlaceIdx = (places: PlaceItem[], dayDateISO: string, nowMs: number): number => {
    let current = -1;
    for (let i = 0; i < places.length; i += 1) {
        const startMs = parsePlaceStartMs(places[i], dayDateISO);
        if (startMs == null) continue;
        if (nowMs >= startMs) current = i;
    }
    // 다음 place 시작 시간이 nowMs 이전이면 그 이전 place 는 past — 이미 current 는 마지막 past 로 저장됨
    // 마지막 past + 다음 place 시작 사이 = current
    if (current >= 0 && current < places.length - 1) {
        const nextStart = parsePlaceStartMs(places[current + 1], dayDateISO);
        if (nextStart != null && nowMs >= nextStart) {
            // 이미 다음 place 로 넘어감 → 재계산 필요 없음, 반복문에서 current 가 갱신됨
        }
    }
    return current;
};

type DayStatus = 'past' | 'current' | 'future';

const computeDayStatus = (dayDateISO: string, nowMs: number): DayStatus => {
    const day = parseISODate(dayDateISO);
    if (!day) return 'future';
    const start = new Date(day);
    start.setHours(0, 0, 0, 0);
    const end = new Date(day);
    end.setHours(23, 59, 59, 999);
    const now = nowMs;
    if (now > end.getTime()) return 'past';
    if (now < start.getTime()) return 'future';
    return 'current';
};

/** N박 M일 문구 */
const formatTripDuration = (startDate: string | null, endDate: string | null, t: (k: string, o?: any) => string): string => {
    if (!startDate) return '';
    const s = parseISODate(startDate);
    const e = parseISODate(endDate || startDate);
    if (!s || !e) return '';
    const ms = e.setHours(0, 0, 0, 0) - s.setHours(0, 0, 0, 0);
    const days = Math.max(0, Math.round(ms / (24 * 60 * 60 * 1000))) + 1;
    const nights = Math.max(0, days - 1);
    return t('meeting.detailV6.durationNightsDays', { nights, days });
};

/** 참여자 중 place 200m 이내 도착 인원 */
const countArrivedAt = (place: PlaceItem, members: MemberView[], locations: MemberLocationMap): { arrived: number; total: number } => {
    if (place.lat == null || place.lng == null) return { arrived: 0, total: members.length };
    let arrived = 0;
    members.forEach((m) => {
        const loc = locations[m.userNo];
        if (!loc || loc.latitude == null || loc.longitude == null) return;
        const d = haversineMeters(loc.latitude, loc.longitude, place.lat as number, place.lng as number);
        if (d < 200) arrived += 1;
    });
    return { arrived, total: members.length };
};

/** place 상태별 표시 텍스트 (지도 데이터 · 카테고리 · 실시간 도착 반영) */
const statusTextForPlace = (
    place: PlaceItem,
    status: PlaceStatus,
    arrived: { arrived: number; total: number },
    t: (k: string, o?: any) => string,
): string => {
    const cat = (place.category || '').toUpperCase();

    if (status === 'past') {
        if (cat === 'HOTEL') return t('meeting.detailV6.st.checkedOut');
        return t('meeting.detailV6.st.done');
    }

    if (status === 'future') {
        if (cat === 'HOTEL') return t('meeting.detailV6.st.checkIn');
        return t('meeting.detailV6.st.planned');
    }

    // current
    if (cat === 'HOTEL') return t('meeting.detailV6.st.staying');
    if (arrived.total > 0) {
        if (arrived.arrived === arrived.total) {
            return t('meeting.detailV6.st.allArrived', { arrived: arrived.arrived, total: arrived.total });
        }
        return t('meeting.detailV6.st.movingIn', { arrived: arrived.arrived, total: arrived.total });
    }
    return t('meeting.detailV6.st.inProgress');
};

/** 장소 한 곳에 할 수 있는 일들. 줄 안에 버튼을 또 넣을 수 없어
 *  (버튼 안 버튼은 안 됩니다) 줄을 누르면 시트에 모아 보입니다. */
interface PlaceMenuTarget {
    /** meeting_sub id — 수정·삭제에 필요합니다 */
    subPlaceId: number | null;
    /** place 테이블 id — 사진·후기에 필요합니다 */
    placeId: string | null;
    name: string;
    /** 다녀온 뒤인가 — 후기 쓰기를 열지 말지 */
    canAdd: boolean;
    /** 어디인지 지도로 보여주기 위한 값 — 없으면 지도를 그리지 않습니다 */
    lat?: number | null;
    lng?: number | null;
    address?: string | null;
}
type OpenPlaceMenu = (target: PlaceMenuTarget) => void;

/** 숙박지 한 줄 — 전체 숙소와 Day별 숙소가 같은 모양입니다.
 *  place 키가 있을 때만 누를 수 있게 합니다 — 열 곳이 없는데 눌리면 아무 일도 안 생기니까요. */
const HotelBadge = ({ hotel, canAdd, onOpenMenu, scope = 'day' }: {
    hotel: {
        name: string; address?: string; placeId?: string | null; subPlaceId?: number | null;
        byNickname?: string | null; checkIn?: string | null; checkOut?: string | null;
        lat?: number | null; lng?: number | null;
    };
    canAdd: boolean;
    onOpenMenu: OpenPlaceMenu;
    /** 여행 전체를 걸치는 숙소인지, 그 날의 숙소인지 — 말이 달라야 구분됩니다 */
    scope?: 'trip' | 'day';
}) => {
    const { t } = useTranslation();

    /* 체크인 ~ 체크아웃. 날짜·시각을 함께 보여줘야 숙소임이 분명해지고
       몇 박인지도 읽힙니다. 하나라도 없으면 줄을 그리지 않습니다. */
    const stayRange = (() => {
        const moment = (iso?: string | null) => {
            if (!iso) return null;
            const d = new Date(iso);
            if (Number.isNaN(d.getTime())) return null;
            return t('meeting.detailV6.stayMoment', {
                /* 다른 곳의 날짜 표기(10/01)와 자릿수를 맞춥니다 */
                month: String(d.getMonth() + 1).padStart(2, '0'),
                day: String(d.getDate()).padStart(2, '0'),
                hour: String(d.getHours()).padStart(2, '0'),
                minute: String(d.getMinutes()).padStart(2, '0'),
            });
        };
        const a = moment(hotel.checkIn);
        const b = moment(hotel.checkOut);
        return a && b ? a + ' \u2192 ' + b : null;
    })();

    const inner = (
        <>
            {/* 관광지는 타임라인의 점으로 표시됩니다. 숙소는 타일로 둬서
                한눈에 다른 종류임이 보이게 합니다. */}
            <span className={cls("v6-hotel-icon")} aria-hidden>
                <IcHotel size={18} />
            </span>
            <span className={cls("v6-hotel-text")}>
                {/* 이름만 있으면 침대 아이콘으로 유추해야 합니다. 무엇인지 적습니다 */}
                <span className={cls("v6-hotel-label")}>
                    {t(scope === 'trip' ? 'meeting.detailV6.stayAllDays' : 'meeting.detailV6.spot.hotel')}
                </span>
                <span className={cls("v6-hotel-name")}>{hotel.name}</span>
                {stayRange ? (
                    <span className={cls("v6-hotel-stay")}>{stayRange}</span>
                ) : null}
                {hotel.address || hotel.byNickname ? (
                    <span className={cls("v6-hotel-sub")}>
                        {hotel.address || ''}
                        {hotel.address && hotel.byNickname ? ' \u00b7 ' : ''}
                        {hotel.byNickname
                            ? t('meeting.detailV6.pickedBy', { who: hotel.byNickname })
                            : ''}
                    </span>
                ) : null}
            </span>
        </>
    );
    if (!hotel.placeId && !hotel.subPlaceId) {
        return <div className={cls("v6-hotel-badge")}>{inner}</div>;
    }
    return (
        <button
            type="button"
            className={cls("v6-hotel-badge tappable")}
            onClick={() => onOpenMenu({
                subPlaceId: hotel.subPlaceId ?? null,
                placeId: hotel.placeId ?? null,
                name: hotel.name,
                canAdd,
                lat: hotel.lat ?? null,
                lng: hotel.lng ?? null,
                address: hotel.address ?? null,
            })}
        >
            {inner}
            <IcChevronRight size={14} />
        </button>
    );
};

// ═══════════════════════ B0 · ParticipantsStrip ═══════════════════════

interface ParticipantsStripProps {
    members: MemberView[];
    onInvite: () => void;
    onDetail: () => void;
}

const ParticipantsStrip = ({ members, onInvite, onDetail }: ParticipantsStripProps) => {
    const { t } = useTranslation();
    return (
        <div className={cls("v6-pstrip")}>
            <div className={cls("v6-pstrip-scroll")}>
                {members.map((m) => (
                    <span key={m.userNo} className={cls(`v6-pchip ${m.status}`)}>
                        <span className={cls(`v6-pchip-avatar ${m.isMe ? 'me' : ''}`)}>{initialsOf(m.name)}</span>
                        {m.isHost ? (
                            <IcCrown size={11} color="var(--t-ink-2)" />
                        ) : null}
                        <span>{m.name}{m.isMe ? ` (${t('common.me')})` : ''}</span>
                        <span className={cls(`v6-pchip-dot ${m.locationSharing ? 'on' : ''}`)} />
                    </span>
                ))}
            </div>
            <div className={cls("v6-pstrip-actions")}>
                <button type="button" className={cls("v6-pstrip-btn")} onClick={onInvite}>
                    <IcPlus size={13} />
                    {t('meeting.invite')}
                </button>
                <button type="button" className={cls("v6-pstrip-btn")} onClick={onDetail}>
                    <IcUsers size={13} />
                    {t('meeting.detail')}
                </button>
            </div>
        </div>
    );
};

// ═══════ B0-1 · MyStatusRow ═══════

/** 참석 상태 — 서버 값과 화면 값의 대응. 한 곳에만 둡니다 */
const ATTEND_OPTIONS: Array<{
    key: AttendanceStatus;
    server: 'ATTENDING' | 'UNDECIDED' | 'NOT_ATTENDING';
    labelKey: string;
}> = [
    { key: 'confirmed', server: 'ATTENDING', labelKey: 'meeting.detailV6.attendYes' },
    { key: 'pending', server: 'UNDECIDED', labelKey: 'meeting.detailV6.attendMaybe' },
    { key: 'declined', server: 'NOT_ATTENDING', labelKey: 'meeting.detailV6.attendNo' },
];

/**
 * 내 참석 상태와 위치 공유 — 상세에서 바로 바꿉니다.
 *
 * 예전에는 바꿀 자리가 없었습니다. 참여자 시트 안에 위치공유 버튼이 있긴
 * 했지만 콜백을 넘겨주지 않아 렌더되지도 않았습니다.
 */
const MyStatusRow = ({ me, busy, onChange }: {
    me: MemberView | null;
    busy: boolean;
    onChange: (next: {
        attendanceStatus?: 'ATTENDING' | 'UNDECIDED' | 'NOT_ATTENDING';
        locationSharing?: boolean;
    }) => void;
}) => {
    const { t } = useTranslation();
    if (!me) return null;
    return (
        <div className={cls("v6-mystatus")}>
            <span className={cls("v6-mystatus-label")}>{t('meeting.detailV6.myStatus')}</span>
            <div className={cls("v6-mystatus-group")}>
                {ATTEND_OPTIONS.map((o) => (
                    <button
                        key={o.key}
                        type="button"
                        className={cls(`v6-mystatus-opt ${me.status === o.key ? 'on' : ''}`)}
                        disabled={busy}
                        onClick={() => onChange({ attendanceStatus: o.server })}
                    >
                        {t(o.labelKey)}
                    </button>
                ))}
            </div>
            <button
                type="button"
                className={cls(`v6-mystatus-share ${me.locationSharing ? 'on' : ''}`)}
                disabled={busy}
                onClick={() => onChange({ locationSharing: !me.locationSharing })}
            >
                <IcPin size={12} />
                {me.locationSharing
                    ? t('meeting.detailV6.sharingOn')
                    : t('meeting.detailV6.sharingOff')}
            </button>
        </div>
    );
};

// ═══════════════════════ B · TripStepper (dot-line 노드) ═══════════════════════

interface TripStepperProps {
    days: DayView[];
    activeIdx: number;
    onSelect: (idx: number) => void;
    globalHotel: HotelInfo | null;
    isPerDayHotel: boolean;
    nowMs: number;
    onOpenMenu: OpenPlaceMenu;
}

const TripStepper = ({
    days, activeIdx, onSelect, globalHotel, isPerDayHotel, nowMs, onOpenMenu,
}: TripStepperProps) => {
    const { t, i18n } = useTranslation();
    if (!days.length) return null;

    const activeDay = days[activeIdx];
    const activeStatus = activeDay ? computeDayStatus(activeDay.date, nowMs) : 'future';
    const activeStatusLabel =
        activeStatus === 'current' ? t('meeting.detailV6.dayInProgress')
        : activeStatus === 'past' ? t('meeting.detailV6.dayPassed')
        : t('meeting.detailV6.dayFuture');

    const fillPct = days.length > 1 ? (activeIdx / (days.length - 1)) * 100 : 0;

    /* 모임명·여행지·기간은 바로 위 히어로가 말합니다.
       여기서 또 말하면 같은 글자가 한 화면에 두 번 쌓입니다. */
    const cardTitle = t('meeting.detailV6.tripFlow');

    return (
        <div className={cls("v6-card")}>
            <div className={cls("v6-card-head")}>
                <div className={cls("v6-card-head-title-group")}>
                    <span className={cls("v6-card-title")}>
                        {cardTitle}
                    </span>
                </div>
                <span className={cls("v6-card-hint")}>
                    Day {activeIdx + 1} {activeStatusLabel}
                </span>
            </div>
            <div className={cls("v6-card-body")}>
                <div className={cls("v6-stepper")}>
                    <div className={cls("v6-stepper-track")}>
                        <div className={cls("v6-stepper-track-fill")} style={{ width: `${fillPct}%` }} />
                    </div>
                    <div className={cls("v6-stepper-nodes")}>
                        {days.map((d, i) => {
                            const status: PlaceStatus =
                                i < activeIdx ? 'past'
                                : i === activeIdx ? 'current'
                                : 'future';
                            const nodeDate = parseISODate(d.date);
                            const weekday = nodeDate ? formatWeekday(nodeDate, i18n.language || 'ko') : '';
                            const primary = d.places[0] || null;
                            return (
                                <button
                                    key={d.date}
                                    type="button"
                                    className={cls(`v6-step-node ${status === 'past' ? 'past' : ''} ${status === 'current' ? 'active' : ''}`)}
                                    onClick={() => onSelect(i)}
                                >
                                    <div className={cls("v6-step-dot")} />
                                    <div className={cls("v6-step-labels")}>
                                        <span className={cls("v6-step-daynum")}>Day {i + 1}</span>
                                        <span className={cls("v6-step-date")}>
                                            {nodeDate ? formatMonthDay(nodeDate) : ''}{weekday ? ` (${weekday})` : ''}
                                        </span>
                                        {primary ? (
                                            <span className={cls("v6-step-place")}>{primary.name}</span>
                                        ) : null}
                                        {primary?.time ? (
                                            <span className={cls("v6-step-time")}>{primary.time}</span>
                                        ) : null}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {!isPerDayHotel && globalHotel ? (
                    /* 전체 숙소는 여행 전체를 걸칩니다 — 첫날이 아직이면 묵을 게 없습니다. */
                    <HotelBadge
                        hotel={globalHotel}
                        scope="trip"
                        canAdd={computeDayStatus(days[0].date, nowMs) !== 'future'}
                        onOpenMenu={onOpenMenu}
                    />
                ) : null}
            </div>
        </div>
    );
};

// ═══════════════════════ C · ActiveDayPanel ═══════════════════════

interface ActiveDayPanelProps {
    day: DayView | null;
    perDayHotel: HotelInfo | null;
    destination: DestinationInfo | null;
    members: MemberView[];
    locations: MemberLocationMap;
    onAddPlace: () => void;
    onAddHotel: () => void;
    /** 끝난 모임 — 일정을 더 손대지 않는다 (추가·집결지 버튼을 감춘다) */
    planLocked: boolean;
    /** 집결지 고르기 — 참여자 누구나 누를 수 있습니다 */
    onSetGather: () => void;
    /** 지금 보여줄 자리 — 시각에 따라 집결지/장소/숙소가 바뀝니다 */
    nowSpot: NowSpot;
    /** 지도를 눌렀을 때 — 우리 지도를 크게 봅니다 */
    onOpenMap: () => void;
    /** 길찾기 — 다른 지도 앱으로 보냅니다. 지도 클릭과는 다른 일입니다 */
    onOpenDirections: () => void;
    onOpenMenu: OpenPlaceMenu;
    nowMs: number;
}

const ActiveDayPanel = ({
    day, perDayHotel, destination, members, locations, onAddPlace, onAddHotel, onSetGather, planLocked,
    nowSpot, onOpenMap, onOpenDirections, onOpenMenu, nowMs,
}: ActiveDayPanelProps) => {
    const { t, i18n } = useTranslation();

    if (!day) {
        return (
            <div className={cls("v6-card")}>
                <div className={cls("v6-card-body")}>
                    <div className={cls("v6-empty")}>
                        <span className={cls("v6-empty-title")}>
                            {t('meeting.detailV6.dayLoading')}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    const dayDate = parseISODate(day.date);
    const weekday = dayDate ? formatWeekday(dayDate, i18n.language || 'ko') : '';
    const status = computeDayStatus(day.date, nowMs);
    /* 지난 일자에는 장소를 담을 수 없습니다 (서버도 거부합니다).
       끝난 모임이면 남은 일자라도 담지 않습니다 — 모임이 이미 끝났습니다. */
    const canAddToDay = !planLocked && status !== 'past';
    const statusLabel =
        status === 'current' ? t('meeting.detailV6.dayInProgress')
        : status === 'past' ? t('meeting.detailV6.dayPassed')
        : t('meeting.detailV6.dayFuture');

    const currentIdx = status === 'current' ? computeCurrentPlaceIdx(day.places, day.date, nowMs) : -1;

    return (
        <div className={cls("v6-card")}>
            <div className={cls("v6-day-head")}>
                <div className={cls("v6-day-head-title-group")}>
                    <span className={cls("v6-day-head-title")}>
                        Day {day.index + 1} · {dayDate ? formatMonthDay(dayDate) : day.date}
                        {weekday ? ` (${weekday})` : ''}
                    </span>
                    {destination?.name ? (
                        <span className={cls("v6-day-head-sub")}>{destination.name}</span>
                    ) : null}
                </div>
                <span className={cls(`v6-status-pill ${status}`)}>{statusLabel}</span>
            </div>

            <div className={cls("v6-card-body")}>
                {/* 미니 지도 — 지금 시점에 볼 곳을 그립니다.
                    출발 전이면 집결지, 여행 중이면 그 시간대 장소, 밤이면 숙소.
                    무엇을 위한 지도인지 위에 한 줄로 밝힙니다. */}
                {(() => {
                    if (nowSpot.kind === 'none') {
                        return (
                            <button type="button" className={cls("v6-day-map")} onClick={onOpenMap}>
                                <div className={cls("v6-day-map-placeholder")}>
                                    {t('meeting.detailV6.mapEmpty')}
                                </div>
                            </button>
                        );
                    }
                    const center = { lat: nowSpot.lat, lng: nowSpot.lng };
                    /* 그날 장소들을 함께 찍되, 지금 자리를 1번으로 둡니다 */
                    const geoPlaces = day.places.filter((p) => p.lat != null && p.lng != null);
                    const markers = nowSpot.kind === 'place'
                        ? geoPlaces.map((p, idx) => ({
                            id: `v6-day-${p.id}`,
                            lat: p.lat as number,
                            lng: p.lng as number,
                            label: String(idx + 1),
                            tooltip: p.name,
                            isDestination: false,
                        }))
                        : [{
                            id: `v6-now-${nowSpot.kind}`,
                            lat: nowSpot.lat,
                            lng: nowSpot.lng,
                            label: '',
                            tooltip: nowSpot.name,
                            isDestination: true,
                        }];
                    const kindLabel = t(`meeting.detailV6.spot.${nowSpot.kind}`);
                    return (
                        <>
                            <div className={cls("v6-now-bar")}>
                                <span className={cls("v6-now-kind")}>{kindLabel}</span>
                                {nowSpot.when ? <span className={cls("v6-now-when")}>{nowSpot.when}</span> : null}
                                {nowSpot.name ? <span className={cls("v6-now-name")}>{nowSpot.name}</span> : null}
                                {nowSpot.byNickname ? (
                                    <span className={cls("v6-now-by")}>
                                        {t('meeting.detailV6.pickedBy', { who: nowSpot.byNickname })}
                                    </span>
                                ) : null}
                            </div>
                            <div className={cls("v6-day-map")} onClick={onOpenMap}>
                                {/* key 로 자리 전환 시 완전 재마운트 — MapContainer 내부 provider 선택
                                    (Kakao 국내 / Leaflet 해외) 이 최초 마운트에 결정되어 이전 자리의
                                    국가 provider 를 잔존시키는 회귀 방지. */}
                                <MapContainer
                                    key={`v6-nowmap-${nowSpot.kind}-${Math.round(center.lat * 10)}-${Math.round(center.lng * 10)}`}
                                    center={center}
                                    markers={markers}
                                    height={160}
                                    zoomLevel={nowSpot.kind === 'destination' ? 7 : 5}
                                    fitBounds={markers.length > 1}
                                    placeName={nowSpot.name}
                                />
                            </div>
                            {/* 길찾기는 따로 둡니다 — 지도를 누르면 우리 지도가 커지고,
                                다른 앱으로 보내는 것은 이 버튼입니다. 예전에는 지도를
                                누르면 앱 목록이 떠서 공유 시트처럼 보였습니다. */}
                            <button
                                type="button"
                                className={cls("v6-map-directions")}
                                onClick={(e) => { e.stopPropagation(); onOpenDirections(); }}
                            >
                                <IcPin size={14} />
                                {t('meeting.detailV6.directions')}
                            </button>
                            {nowSpot.needsGather && !planLocked ? (
                                <button type="button" className={cls("v6-now-cta")} onClick={onSetGather}>
                                    {t('meeting.detailV6.gatherMissing')}
                                </button>
                            ) : null}
                        </>
                    );
                })()}

                {perDayHotel ? (
                    <HotelBadge
                        hotel={perDayHotel}
                        canAdd={status !== 'future'}
                        onOpenMenu={onOpenMenu}
                    />
                ) : null}

                {day.places.length === 0 ? (
                    <div className={cls("v6-empty")}>
                        <span className={cls("v6-empty-title")}>
                            {t('meeting.detailV6.emptyTitle')}
                        </span>
                        <span className={cls("v6-empty-desc")}>
                            {t('meeting.detailV6.emptyDesc')}
                        </span>
                        {/* 장소를 추가하는 자리다. 예전에는 여기에 「투표 시작」이
                            조건 없이 있었는데, 그건 날짜·시간을 정하는 일정 투표로
                            보내는 버튼이라 성격이 다르고, 날짜가 이미 확정된 모임에도
                            보였다. 서버도 같은 기준이다 — 일정이 확정되면
                            (confirmedScheduleId != null) 투표는 열리지 않는다. */}
                        {/* 지난 일자에는 버튼을 아예 보여주지 않습니다. 서버가 거부하고
                            모달의 일차 칩도 비활성화되지만, 버튼이 보이면 눌러 보고
                            나서야 안 된다는 걸 알게 됩니다. */}
                        {canAddToDay ? (
                            <div className={cls("v6-empty-actions")}>
                                <button type="button" className={cls("v6-btn-primary")} onClick={onAddPlace}>
                                    <IcPlus size={14} />
                                    {t('meeting.detailV6.addPlace')}
                                </button>
                                <button type="button" className={cls("v6-btn-ghost")} onClick={onAddHotel}>
                                    <IcHotel size={14} />
                                    {t('meeting.detailV6.addHotel')}
                                </button>
                            </div>
                        ) : (
                            <span className={cls("v6-empty-desc")}>
                                {t('meeting.detailV6.pastDayNoAdd')}
                            </span>
                        )}
                    </div>
                ) : (
                    <>
                        <div className={cls("v6-timeline")}>
                            {day.places.map((p, idx) => {
                                const pStatus: PlaceStatus =
                                    status === 'past' ? 'past'
                                    : status === 'future' ? 'future'
                                    : idx < currentIdx ? 'past'
                                    : idx === currentIdx ? 'current'
                                    : 'future';
                                const arrived = pStatus === 'current'
                                    ? countArrivedAt(p, members, locations)
                                    : { arrived: 0, total: members.length };
                                const stText = statusTextForPlace(p, pStatus, arrived, t);
                                /* 가보기 전에는 후기를 막습니다 — 일반 모임 상세와 같은 기준입니다. */
                                const canAdd = pStatus !== 'future';
                                const inner = (
                                    <>
                                        <div className={cls(`v6-tl-time ${pStatus} ${p.time ? '' : 'empty'}`)}>
                                            {p.time || '--:--'}
                                        </div>
                                        <div className={cls("v6-tl-dotcol")}>
                                            <div className={cls(`v6-tl-dot ${pStatus}`)} />
                                        </div>
                                        <div className={cls("v6-tl-body")}>
                                            {p.category ? (
                                                <div className={cls("v6-tl-cat")}>
                                                    {/* 분류 코드를 그대로 찍고 있었습니다 — 화면에
                                                        ATTRACTION 처럼 영어가 그대로 나왔습니다.
                                                        같은 줄의 숙소는 「숙소」로 나와 더 도드라졌습니다. */}
                                                    {getCategoryLabel(p.category)}
                                                </div>
                                            ) : null}
                                            <div className={cls(`v6-tl-name ${pStatus}`)}>{p.name}</div>
                                            <div className={cls(`v6-tl-status ${pStatus}`)}>{stText}</div>
                                            {p.byNickname ? (
                                                <div className={cls("v6-tl-by")}>
                                                    {t('meeting.detailV6.pickedBy', { who: p.byNickname })}
                                                </div>
                                            ) : null}
                                        </div>
                                    </>
                                );
                                /* 사진·후기는 place 키가 있어야 하지만 수정·삭제는 서브 장소
                                   id 만 있으면 됩니다. 그래서 줄은 항상 누를 수 있습니다. */
                                return (
                                    <button
                                        key={p.id}
                                        type="button"
                                        className={cls("v6-timeline-row tappable")}
                                        onClick={() => onOpenMenu({
                                            subPlaceId: Number(p.id) || null,
                                            placeId: p.placeId ?? null,
                                            name: p.name,
                                            canAdd,
                                            lat: p.lat ?? null,
                                            lng: p.lng ?? null,
                                            address: p.address ?? null,
                                        })}
                                    >
                                        {inner}
                                        <span className={cls("v6-tl-photo")}>
                                            <IcCamera size={14} />
                                            <IcChevronRight size={14} />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        {/* 빈 날의 버튼과 같은 규칙입니다. 여기만 조건이 없어서
                            지난 일자·끝난 모임에도 「관광지 추가」가 남아 있었습니다. */}
                        {canAddToDay ? (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button type="button" className={cls("v6-add-cta")} onClick={onAddPlace} style={{ flex: 1 }}>
                                    <IcPlus size={14} />
                                    {t('meeting.detailV6.addPlace')}
                                </button>
                                <button type="button" className={cls("v6-add-cta")} onClick={onAddHotel} style={{ flex: 1 }}>
                                    <IcHotel size={14} />
                                    {t('meeting.detailV6.addHotel')}
                                </button>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};

// ═══════════════════════ D · DaySummaryCard (한 줄 요약) ═══════════════════════

interface DaySummaryCardProps {
    day: DayView;
    onSelect: (idx: number) => void;
    nowMs: number;
}

const DaySummaryCard = ({ day, onSelect, nowMs }: DaySummaryCardProps) => {
    const { t, i18n } = useTranslation();
    const dayDate = parseISODate(day.date);
    const weekday = dayDate ? formatWeekday(dayDate, i18n.language || 'ko') : '';
    const status = computeDayStatus(day.date, nowMs);

    return (
        <button
            type="button"
            className={cls(`v6-day-summary ${status}`)}
            onClick={() => onSelect(day.index)}
        >
            <span className={cls("v6-day-summary-daynum")}>Day {day.index + 1}</span>
            <span className={cls("v6-day-summary-date")}>
                {dayDate ? formatMonthDay(dayDate) : day.date}{weekday ? ` (${weekday})` : ''}
            </span>
            <span className={cls("v6-day-summary-count")}>
                {t('meeting.detailV6.placesCount', { n: day.places.length })}
                {day.hotel?.name ? ` · ${day.hotel.name}` : ''}
            </span>
            <span className={cls(`v6-status-pill ${status}`)} style={{ padding: '2px 8px', fontSize: 10 }}>
                {status === 'current' ? t('meeting.detailV6.dayInProgress')
                    : status === 'past' ? t('meeting.detailV6.dayPassed')
                    : t('meeting.detailV6.dayFuture')}
            </span>
        </button>
    );
};

// ═══════════════════════ E · ExtrasStrip ═══════════════════════

interface ExtrasStripProps {
    onChat: () => void;
    onGame: () => void;
    onExtend: () => void;
    onSetGather: () => void;
    onEnd: () => void;
    canEnd: boolean;
    canExtend: boolean;
    /** 여행 모임 — 종료시각 '연장' 대신 '집결지' 를 놓습니다 */
    showGather: boolean;
}

const ExtrasStrip = ({
    onChat, onGame, onExtend, onSetGather, onEnd, canEnd, canExtend, showGather,
}: ExtrasStripProps) => {
    const { t } = useTranslation();
    const visible: Array<'chat' | 'game' | 'extend' | 'gather' | 'end'> = ['chat', 'game'];
    /* 여행은 날짜 범위로 굴러가서 '종료시각 연장' 이 할 일이 없습니다.
       그 자리에 집결지를 놓고, 방장만이 아니라 참여자 누구나 정할 수 있게 둡니다
       (백엔드 PUT /meeting 도 참여 멤버면 허용합니다). */
    if (showGather) visible.push('gather');
    else if (canExtend) visible.push('extend');
    if (canEnd) visible.push('end');
    return (
        <div className={cls("v6-card")}>
            <div className={cls("v6-card-head")}>
                <div className={cls("v6-card-head-title-group")}>
                    <span className={cls("v6-card-title")}>
                        {t('meeting.detailV6.extras')}
                    </span>
                </div>
            </div>
            <div className={cls("v6-card-body")}>
                <div className={cls("v6-extras")} style={{ gridTemplateColumns: `repeat(${visible.length}, 1fr)` }}>
                    {visible.includes('chat') ? (
                        <button type="button" className={cls("v6-extra-btn")} onClick={onChat}>
                            <IcChat size={20} />
                            <span>{t('meeting.chat')}</span>
                        </button>
                    ) : null}
                    {visible.includes('game') ? (
                        <button type="button" className={cls("v6-extra-btn")} onClick={onGame}>
                            <IcDice size={20} />
                            <span>{t('meeting.miniGame')}</span>
                        </button>
                    ) : null}
                    {visible.includes('gather') ? (
                        <button type="button" className={cls("v6-extra-btn")} onClick={onSetGather}>
                            <IcPin size={20} />
                            <span>{t('meetingForm.gatherPlace')}</span>
                        </button>
                    ) : null}
                    {visible.includes('extend') ? (
                        <button type="button" className={cls("v6-extra-btn")} onClick={onExtend}>
                            <IcClock size={20} />
                            <span>{t('meeting.extend')}</span>
                        </button>
                    ) : null}
                    {visible.includes('end') ? (
                        <button type="button" className={cls("v6-extra-btn end")} onClick={onEnd}>
                            <IcClose size={20} />
                            <span>{t('meeting.end')}</span>
                        </button>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════ F · InviteSheet ═══════════════════════

interface InviteSheetProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    meetingId: string | undefined;
    inviteCode: string | null;
    onShare: () => void;
    onOpenInviteFromPast: () => void;
    externallyInvitedUserNos?: number[];
}

const InviteSheet = ({
    isOpen, setIsOpen, meetingId, inviteCode, onShare, onOpenInviteFromPast, externallyInvitedUserNos,
}: InviteSheetProps) => {
    const { t } = useTranslation();
    return (
        <BottomSheet isOpen={isOpen} setIsOpen={setIsOpen}>
            <div className={cls("v6-sheet-header")}>
                <span className={cls("v6-sheet-title")}>{t('meetingInvite.friendInvite')}</span>
                <button type="button" className={cls("v6-pstrip-btn")} onClick={() => setIsOpen(false)} aria-label="close">
                    <IcClose size={14} />
                </button>
            </div>
            <div className={cls("v6-sheet-body")}>
                {meetingId ? (
                    <MeetingInviteSection
                        meetingId={meetingId}
                        inviteCode={inviteCode || undefined}
                        onShare={onShare}
                        onOpenInviteFromPast={onOpenInviteFromPast}
                        withSectionPadding={false}
                        showHeaders={false}
                        externallyInvitedUserNos={externallyInvitedUserNos}
                    />
                ) : null}
            </div>
        </BottomSheet>
    );
};

// ═══════════════════════ F2 · PlaceMenuSheet ═══════════════════════

/** 장소 한 곳으로 할 수 있는 일 — 사진·후기 · 수정 · 삭제.
 *
 *  target 을 시트가 닫힐 때 같이 지우지 않는 이유는, 닫히는 동안
 *  이름이 사라져 빈 시트가 미끄러져 내려가기 때문입니다. */
const PlaceMenuSheet = ({ isOpen, setIsOpen, target, onPhotos, onEdit, onDelete, planLocked }: {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    target: PlaceMenuTarget | null;
    onPhotos: (t: PlaceMenuTarget) => void;
    onEdit: (subPlaceId: number) => void;
    onDelete: (subPlaceId: number, name: string) => void;
    /** 끝난 모임 — 수정·삭제를 감춘다 (사진·후기는 그대로) */
    planLocked: boolean;
}) => {
    const { t } = useTranslation();
    /* 좌표가 둘 다 있을 때만 지도를 그립니다 */
    const hasSpot = Number.isFinite(Number(target?.lat)) && Number.isFinite(Number(target?.lng))
        && Number(target?.lat) !== 0 && Number(target?.lng) !== 0;
    return (
        <BottomSheet isOpen={isOpen} setIsOpen={setIsOpen}>
            <div className={cls("v6-sheet-header")}>
                <span className={cls("v6-sheet-title")}>{target?.name || ''}</span>
                <button type="button" className={cls("v6-pstrip-btn")} onClick={() => setIsOpen(false)} aria-label="close">
                    <IcClose size={14} />
                </button>
            </div>
            <div className={cls("v6-sheet-body")}>
                {/* 이름만으로는 "어디였더라" 를 떠올려야 합니다. 지도를 함께 둡니다.
                    좌표가 없는 예전 장소는 그리지 않습니다 — 빈 네모만 남습니다. */}
                {hasSpot ? (
                    <div className={cls("v6-place-menu-map")}>
                        <MapContainer
                            key={`v6-menu-${target?.subPlaceId ?? target?.placeId ?? ''}`}
                            center={{ lat: Number(target?.lat), lng: Number(target?.lng) }}
                            markers={[{
                                id: target?.subPlaceId ?? target?.placeId ?? 'spot',
                                lat: Number(target?.lat),
                                lng: Number(target?.lng),
                                label: (target?.name || '').slice(0, 1),
                                tooltip: target?.name || '',
                                isDestination: true,
                            }]}
                            height={150}
                            zoomLevel={4}
                            placeName={target?.name}
                        />
                    </div>
                ) : null}
                {target?.address ? (
                    <p className={cls("v6-place-menu-addr")}>{target.address}</p>
                ) : null}
                <div className={cls("v6-place-menu")}>
                    {target?.placeId ? (
                        <button
                            type="button"
                            className={cls("v6-extra-btn")}
                            onClick={() => { setIsOpen(false); onPhotos(target); }}
                        >
                            <IcCamera size={20} />
                            <span>{t('visitPhoto.seeAll')}</span>
                        </button>
                    ) : null}
                    {/* 끝난 모임의 일정은 고치지 않습니다 — 사진·후기는 그대로 봅니다 */}
                    {target?.subPlaceId && !planLocked ? (
                        <>
                            <button
                                type="button"
                                className={cls("v6-extra-btn")}
                                onClick={() => { setIsOpen(false); onEdit(target.subPlaceId as number); }}
                            >
                                <IcEdit size={20} />
                                <span>{t('meeting.editLabel')}</span>
                            </button>
                            <button
                                type="button"
                                className={cls("v6-extra-btn end")}
                                onClick={() => { setIsOpen(false); onDelete(target.subPlaceId as number, target.name); }}
                            >
                                <IcTrash size={20} />
                                <span>{t('meeting.deleteExpenseBtn')}</span>
                            </button>
                        </>
                    ) : null}
                </div>
            </div>
        </BottomSheet>
    );
};

// ═══════════════════════ G · ParticipantDetailSheet ═══════════════════════

const formatDistance = (meters: number): string => {
    if (!Number.isFinite(meters)) return '-';
    if (meters < 1000) return `${Math.round(meters)}m`;
    return `${(meters / 1000).toFixed(1)}km`;
};

/** 그날 날짜 + 장소 시각 → ISO. 시각이 없으면 null (지각 판정을 하지 않습니다) */
const scheduledIsoOf = (dayDateISO?: string | null, time?: string | null): string | null => {
    if (!dayDateISO || !time) return null;
    const mins = parseTimeToMinutes(time);
    if (mins == null) return null;
    const d = new Date(dayDateISO);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
    return toLocalISOString(d);
};

const formatRelativeMinutes = (ts: number | null | undefined, now: number, t: (k: string, o?: any) => string): string => {
    if (!ts) return '-';
    const minutes = Math.max(0, Math.floor((now - ts) / 60000));
    if (minutes === 0) return t('meeting.detailV6.justNow');
    if (minutes < 60) return t('meeting.detailV6.minutesAgo', { n: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('meeting.detailV6.hoursAgo', { n: hours });
    const days = Math.floor(hours / 24);
    return t('meeting.detailV6.daysAgo', { n: days });
};

interface ParticipantDetailSheetProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    members: MemberView[];
    locations: MemberLocationMap;
    destination: DestinationInfo | null;
    onOpenChat: (userNo: number) => void;
    onOpenMap: (userNo: number) => void;
    onToggleMyLocationSharing?: () => void;
}

const ParticipantDetailSheet = ({
    isOpen, setIsOpen, members, locations, destination, onOpenChat, onOpenMap, onToggleMyLocationSharing,
}: ParticipantDetailSheetProps) => {
    const { t } = useTranslation();
    const now = Date.now();
    const sharingCount = members.filter((m) => m.locationSharing).length;

    return (
        <BottomSheet isOpen={isOpen} setIsOpen={setIsOpen}>
            <div className={cls("v6-sheet-header")}>
                <div>
                    <div className={cls("v6-sheet-title")}>
                        {t('meeting.detailV6.participantDetail')}
                    </div>
                    <div className={cls("v6-sheet-sub")}>
                        {t('meeting.detailV6.sharingSummary', { count: sharingCount, total: members.length })}
                    </div>
                </div>
                <button type="button" className={cls("v6-pstrip-btn")} onClick={() => setIsOpen(false)} aria-label="close">
                    <IcClose size={14} />
                </button>
            </div>
            <div className={cls("v6-sheet-body")}>
                {members.map((m) => {
                    const loc = locations[m.userNo];
                    const hasLoc = !!loc && loc.latitude != null && loc.longitude != null;
                    const distanceMeters = hasLoc && destination
                        ? haversineMeters(loc.latitude, loc.longitude, destination.latitude, destination.longitude)
                        : null;

                    return (
                        <div key={m.userNo} className={cls(`v6-detail-row ${m.isMe ? 'me' : ''}`)}>
                            <span className={cls(`v6-detail-avatar ${m.isMe ? 'me' : ''}`)}>{initialsOf(m.name)}</span>
                            <div className={cls("v6-detail-main")}>
                                <div className={cls("v6-detail-name")}>
                                    <span>{m.name}{m.isMe ? ` (${t('common.me')})` : ''}</span>
                                    {m.isHost ? (
                                        <span className={cls("v6-sheet-sub")}>
                                            · {t('meeting.host')}
                                        </span>
                                    ) : null}
                                </div>
                                <div className={cls("v6-detail-meta")}>
                                    {m.locationSharing
                                        ? t('meeting.detailV6.sharingOn')
                                        : t('meeting.detailV6.sharingOff')}
                                    {hasLoc ? ` · ${formatRelativeMinutes(loc.timestamp, now, t)}` : ''}
                                    {distanceMeters != null ? ` · ${formatDistance(distanceMeters)}` : ''}
                                </div>
                                <div className={cls("v6-detail-actions")}>
                                    {m.isMe ? (
                                        onToggleMyLocationSharing ? (
                                            <button type="button" onClick={onToggleMyLocationSharing}>
                                                {m.locationSharing
                                                    ? t('meeting.detailV6.sharingTurnOff')
                                                    : t('meeting.detailV6.sharingTurnOn')}
                                            </button>
                                        ) : null
                                    ) : (
                                        <>
                                            <button type="button" onClick={() => onOpenChat(m.userNo)}>
                                                <IcChat size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                {t('meeting.chat')}
                                            </button>
                                            <button type="button" onClick={() => onOpenMap(m.userNo)} disabled={!hasLoc}>
                                                <IcPin size={11} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                                                {t('meeting.detailV6.mapView')}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </BottomSheet>
    );
};

// ═══════════════════════ MeetingDetailV6 (진입) ═══════════════════════

export const MeetingDetailV6 = () => {
    const { t, i18n } = useTranslation();
    /* 기록 작성기에 내 프로필을 보여 주려고 — 일반 모임 상세와 같은 훅입니다 */
    const { nickname: myNickname, profileImgPath: myProfileUrl } = useUserInfo();
    const navigate = useNavigate();
    /* 장소 검색·시간 선택 화면이 고른 값을 state 에 실어 돌려보냅니다 */
    const location = useLocation();
    const { showAlert, showConfirm } = useAlert();
    const { meetingId: raw } = useParams();
    const meetingId = typeof raw === 'string' ? raw : undefined;

    const trip = useTripView(meetingId);

    /* 제목 배경 사진 — 일반 모임 상세·완료 화면과 같은 자리입니다.
       여행은 다녀온 곳이 여러 곳이라 대표 장소(집결지) 에 모인 사진을 씁니다.
       하나도 없으면 HeroBanner 가 먹색 면으로 떨어져 글자는 그대로 읽힙니다. */
    const placeFeed = usePlaceReviews(trip.placeKey);
    /* 이 모임에 올라온 기록 요약 — 사진 몇 장과 최근 한 건 */
    const { summary: journalSummary, reload: reloadJournalSummary } = useJournalSummary(meetingId);

    /* 내 멤버 정보 — 참석 상태와 위치 공유를 상세에서 바로 바꿉니다 */
    const me = useMemo(() => trip.members.find((m) => m.isMe) || null, [trip.members]);
    const [statusBusy, setStatusBusy] = useState(false);
    const changeMyStatus = useCallback(async (next: {
        attendanceStatus?: 'ATTENDING' | 'UNDECIDED' | 'NOT_ATTENDING';
        locationSharing?: boolean;
    }) => {
        if (!meetingId || statusBusy) return;
        setStatusBusy(true);
        try {
            const res: any = await MeetingRepository.onPutMyMembership(meetingId, next);
            /* onRequest 는 에러를 던지지 않습니다 — 상태 코드를 봐야 실패를 압니다 */
            const ok = typeof res?.status === 'number' && res.status >= 200 && res.status < 300;
            if (!ok) {
                await showAlert({ message: res?.data?.message || t('meeting.detailV6.myStatusFail') });
                return;
            }
            await meetingStore.onGetMeetingMembers(meetingId);
        } finally {
            setStatusBusy(false);
        }
    }, [meetingId, statusBusy, showAlert, t]);
    const heroImages = useMemo(() => feedPhotos(placeFeed), [placeFeed]);

    /* 일정의 장소 한 줄·숙박지를 누르면 뭐를 할지 고르는 시트가 뜽니다.
       줄 안에 버튼을 따로 넣을 수 없어서(버튼 안 버튼) 한 자리에 모았습니다. */
    const [placeMenu, setPlaceMenu] = useState<PlaceMenuTarget | null>(null);
    const [placeMenuOpen, setPlaceMenuOpen] = useState(false);
    const openPlaceMenu = useCallback((target: PlaceMenuTarget) => {
        setPlaceMenu(target);
        setPlaceMenuOpen(true);
    }, []);

    /* 여행은 다녀온 곳이 여러 곳이라 모임 상세처럼 한 장소를 미리 펼치면
       장소 수만큼 조회가 늘어납니다. 여기서는 길만 내주고 불러오는 건 간 화면이 합니다. */
    const openPlacePhotos = useCallback((target: PlaceMenuTarget) => {
        if (!target.placeId) return;
        navigate(placePhotosPath(target.placeId, {
            placeName: target.name, meetingId, canAdd: target.canAdd,
        }));
    }, [navigate, meetingId]);

    const [inviteOpen, setInviteOpen] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [showInviteFromPast, setShowInviteFromPast] = useState(false);
    const [showLadder, setShowLadder] = useState(false);
    /* 미니지도를 눌렀을 때 뜨는 외부 지도앱 고르기 */
    const [showExternalMap, setShowExternalMap] = useState(false);
    const [isEnding, setIsEnding] = useState(false);
    const [invitedFromPastUserNos, setInvitedFromPastUserNos] = useState<number[]>([]);

    // SubPlaceModal 상태 (신규/편집) — mode 로 관광/숙박 분기
    const [showSubPlaceModal, setShowSubPlaceModal] = useState(false);
    /* 변경 이력 시트 — 「마지막 변경」 한 줄을 누르면 열립니다 */
    const [historyOpen, setHistoryOpen] = useState(false);
    const [subPlaceMode, setSubPlaceMode] = useState<'place' | 'hotel'>('place');
    const [editingSubPlace, setEditingSubPlace] = useState<SubPlace | null>(null);
    const [subPlaceForm, setSubPlaceForm] = useState<SubPlaceForm | undefined>(undefined);
    const [isCustomVisitTime, setIsCustomVisitTime] = useState(false);

    /* 서브 장소 수정 — 모달이 editingSubPlace 에서 값을 채우므로
       initialForm 은 비워 둘니다. 일반 모임 상세와 같은 순서입니다. */
    const handleEditSubPlace = useCallback((subPlaceId: number) => {
        const sp = trip.rawSubPlaces.find((x) => Number(x.subPlaceId) === subPlaceId);
        if (!sp) return;
        setEditingSubPlace(sp);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
        setSubPlaceMode(sp.category === 'HOTEL' ? 'hotel' : 'place');
        setShowSubPlaceModal(true);
    }, [trip.rawSubPlaces]);

    const handleDeleteSubPlace = useCallback(async (subPlaceId: number, name: string) => {
        if (!meetingId) return;
        const ok = await showConfirm({
            message: t('meeting.deleteSubPlaceConfirm', { name }),
            confirmText: t('meeting.deleteExpenseBtn'),
        });
        if (!ok) return;
        try {
            const res: any = await MeetingRepository.onDeleteSubPlace(meetingId, subPlaceId);
            /* onRequest 는 예외를 던지지 않습니다 — 상태를 봐야 실패를 압니다.
               보지 않으면 서버가 거부해도 "삭제했어요" 가 뜨고 목록만 그대로입니다. */
            if (!isOkResponse(res)) {
                await showAlert({ message: errorMessageOf(res) || t('meeting.placeDeleteFailed') });
                return;
            }
            await showAlert({ message: t('meeting.placeDeleted') });
            await trip.reloadSubPlaces();
        } catch {
            await showAlert({ message: t('meeting.placeDeleteFailed') });
        }
    }, [meetingId, showConfirm, showAlert, t, trip.reloadSubPlaces]);

    const handleAddPlace = useCallback(() => {
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
        setSubPlaceMode('place');
        setShowSubPlaceModal(true);
    }, []);

    /* 집결지 고르기 — 모임 수정 화면의 집결지 지도를 그대로 엽니다.
       거기에 장소 고르기와 집결 시각 입력이 이미 있고, 저장도 PUT /meeting 한 번입니다.
       백엔드 권한이 "참여 멤버 누구나" 라서 참여자도 정할 수 있습니다. */
    const handleSetGather = useCallback(() => {
        if (!meetingId) return;
        navigate(`/meeting/edit/${meetingId}`, { state: { openGather: true } });
    }, [meetingId, navigate]);

    const handleAddHotel = useCallback(() => {
        setEditingSubPlace(null);
        setSubPlaceMode('hotel');
        let defaultCheckInDate = '';
        let defaultCheckOutDate = '';
        let defaultCheckInTime = '';
        let defaultCheckOutTime = '';
        if (trip.travelPeriod) {
            const pad = (n: number) => String(n).padStart(2, '0');
            const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            const existingCheckOuts = (trip.rawSubPlaces || [])
                .filter((sp) => sp.category === 'HOTEL')
                .map((sp) => (sp.endDatetime || '').slice(0, 10))
                .filter((d): d is string => !!d)
                .sort();
            const lastCheckOut = existingCheckOuts.length > 0
                ? existingCheckOuts[existingCheckOuts.length - 1]
                : null;
            /* 여행이 이미 시작됐으면 기본 체크인을 오늘로 올립니다.
               예전에는 늘 여행 시작일을 기본값으로 뒀는데, 시작일이 지난 모임에서는
               기본값 자체가 과거라 저장이 막혔습니다(지난 날짜 등록 금지). */
            const todayStr = fmt(new Date());
            const rawCheckIn = lastCheckOut || fmt(trip.travelPeriod.start);
            defaultCheckInDate = rawCheckIn < todayStr ? todayStr : rawCheckIn;
            const rawCheckOut = fmt(trip.travelPeriod.end);
            defaultCheckOutDate = rawCheckOut < defaultCheckInDate
                ? defaultCheckInDate : rawCheckOut;
            defaultCheckInTime = '15:00';
            defaultCheckOutTime = '11:00';
        }
        setSubPlaceForm({
            placeName: '',
            placeAddress: '',
            category: 'HOTEL',
            dayNumber: 1,
            dayNumbers: [1],
            visitTime: '',
            description: '',
            checkInDate: defaultCheckInDate,
            checkInTime: defaultCheckInTime,
            checkOutDate: defaultCheckOutDate,
            checkOutTime: defaultCheckOutTime,
        });
        setIsCustomVisitTime(false);
        setShowSubPlaceModal(true);
    }, [trip.travelPeriod, trip.rawSubPlaces]);

    const handleCloseSubPlaceModal = useCallback(() => {
        setShowSubPlaceModal(false);
        setEditingSubPlace(null);
        setSubPlaceForm(undefined);
        setIsCustomVisitTime(false);
    }, []);

    /* 장소 검색·추천 장소·시간 선택 화면에서 돌아왔을 때 — 고른 값을 창에 되살립니다.
       그 화면들은 navigate(returnTo, { state }) 로 고른 값을 들고 돌아오는데,
       여기서 받아 주지 않으면 창이 닫힌 채라 고른 장소가 그냥 사라집니다.
       (일반 모임 상세에는 같은 처리가 있었고 여행 상세에만 빠져 있었습니다 —
        「지도에서 직접 검색」으로 고르면 등록이 안 되던 원인) */
    const [returnHandled, setReturnHandled] = useState(false);
    useEffect(() => {
        const st = location.state as any;
        if (!st?.fromSubPlaceModal || returnHandled) return;
        /* 수정 중이던 장소는 목록이 와야 찾을 수 있습니다 —
           먼저 열면 수정이 신규 등록으로 저장됩니다 */
        if (st.editingSubPlaceId && trip.rawSubPlaces.length === 0) return;

        const num = (v: any): number | undefined => {
            const n = typeof v === 'number' ? v : (v ? parseFloat(v) : NaN);
            /* 0 은 좌표가 아니라 "아직 없음" 입니다 — 검색 화면이 자리값으로 0 을 씁니다 */
            return Number.isFinite(n) && n !== 0 ? n : undefined;
        };
        const saved: SubPlaceForm | undefined = st.subPlaceForm;
        const pi = st.placeInfo;
        let next = saved;

        if (st.selectedTime && saved) {
            next = { ...saved, visitTime: st.selectedTime };
            setIsCustomVisitTime(true);
        } else {
            if (pi) {
                const base: SubPlaceForm = saved || {
                    placeName: '',
                    placeAddress: '',
                    category: 'ATTRACTION',
                    dayNumber: 1,
                    visitTime: '',
                    description: '',
                };
                const lat = num(pi.lat);
                const lng = num(pi.lng);
                next = {
                    ...base,
                    placeName: pi.name || base.placeName,
                    placeAddress: pi.address || base.placeAddress,
                    /* 좌표가 빠지면 미니지도와 "다음 이동 장소" 표시가 깨집니다 */
                    ...(lat !== undefined ? { latitude: lat } : {}),
                    ...(lng !== undefined ? { longitude: lng } : {}),
                    ...(pi.externalPlaceId || pi.placeId
                        ? { placeId: pi.externalPlaceId || pi.placeId } : {}),
                };
            }
            setIsCustomVisitTime(!!st.isCustomVisitTime);
        }

        const editing = st.editingSubPlaceId
            ? trip.rawSubPlaces.find(
                (x) => Number(x.subPlaceId) === Number(st.editingSubPlaceId))
            : undefined;

        if (next) setSubPlaceForm(next);
        if (editing) setEditingSubPlace(editing);
        /* 숙박을 고치던 중이었으면 숙박 모드로 돌아와야 합니다 —
           관광 모드로 열면 숙박 항목이 아예 안 보입니다 */
        const category = next?.category || editing?.category;
        setSubPlaceMode(category === 'HOTEL' ? 'hotel' : 'place');
        setShowSubPlaceModal(true);
        setReturnHandled(true);
    }, [location.state, trip.rawSubPlaces, returnHandled]);

    // 1분마다 nowMs 갱신 — 진행 중 place / Day 상태 자동 rerender
    const [nowMs, setNowMs] = useState<number>(() => Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!meetingId) return;
        meetingStore.onGetMeetingDetail(meetingId);
        meetingStore.onGetMeetingMembers(meetingId);
    }, [meetingId]);

    // activeDayIdx 를 오늘로 자동 세팅 (여행 기간 안일 때) — 최초 로드만
    const [autoActiveDaySet, setAutoActiveDaySet] = useState(false);
    useEffect(() => {
        if (autoActiveDaySet) return;
        if (!trip.days.length) return;
        const now = Date.now();
        const todayIdx = trip.days.findIndex((d) => computeDayStatus(d.date, now) === 'current');
        if (todayIdx >= 0 && todayIdx !== trip.activeDayIdx) {
            trip.setActiveDay(todayIdx);
        }
        setAutoActiveDaySet(true);
    }, [trip.days, trip.activeDayIdx, trip.setActiveDay, autoActiveDaySet]);

    // 실시간 위치는 참여자 상세 시트가 열려있거나 활성 Day 가 진행중일 때 STOMP 구독
    const activeDay = useMemo<DayView | null>(
        () => trip.days[trip.activeDayIdx] || null,
        [trip.days, trip.activeDayIdx],
    );
    const activeDayStatus = activeDay ? computeDayStatus(activeDay.date, nowMs) : 'future';
    /* 미니지도가 지금 무엇을 보여줄지 — 날짜·시각 다듬기는 화면이 i18n 을 쥐고 있어 주입합니다 */
    const formatWhen = useCallback((date: string | null, time: string | null) => {
        if (!date && !time) return null;
        const d = date ? parseISODate(date) : null;
        /* 화면 다른 곳의 Day 머리글과 같은 모양 — "09/21 (월)" */
        const dateLabel = d
            ? `${formatMonthDay(d)} (${formatWeekday(d, i18n.language || 'ko')})`
            : null;
        return [dateLabel, time].filter(Boolean).join(' · ') || null;
    }, [i18n.language]);

    const nowSpot = useNowSpot({
        gather: trip.gather,
        days: trip.days,
        destination: trip.destination,
        globalHotel: trip.globalHotel,
        nowMs,
        formatWhen,
    });

    /* 기록을 남길 자리 — nowSpot 이 고른 그 장소입니다.
       여행이 시작되기 전에는 남길 것이 없으므로 앵커를 주지 않습니다(섹션이 숨습니다).
       placeId 가 없는 장소(직접 입력 등)도 사진·후기를 붙일 수 없어 제외합니다. */
    const journalAnchor = useMemo(() => {
        if (trip.days.length === 0) return null;
        const firstDayStatus = computeDayStatus(trip.days[0].date, nowMs);
        if (firstDayStatus === 'future') return null;

        const spot = nowSpot.place;
        if (spot?.placeId) {
            const dayLabel = spot.dayIndex >= 0
                ? t('meeting.detailV6.dayLabel', { day: spot.dayIndex + 1, defaultValue: `${spot.dayIndex + 1}일차` })
                : null;
            return {
                placeId: spot.placeId,
                placeName: spot.name,
                dayNumber: spot.dayIndex >= 0 ? spot.dayIndex + 1 : undefined,
                subId: typeof spot.id === 'number' ? spot.id : undefined,
                label: dayLabel ? `${dayLabel} · ${spot.name}` : spot.name,
            };
        }

        /* 일정 장소가 없으면 집결지에 붙입니다.
           일정을 아직 안 짠 모임이 적지 않고(개발 DB 의 진행 중 여행도
           그날 장소가 없습니다), 그때 기록을 아예 못 남기는 것은 기획과
           어긋납니다 — 진행 중이면 어디서든 남길 수 있어야 합니다.
           일차·subId 는 붙이지 않습니다. 없는 일정에 매다는 것이 되니까요. */
        if (trip.placeKey && trip.gather?.name) {
            return {
                placeId: trip.placeKey,
                placeName: trip.gather.name,
                label: trip.gather.name,
            };
        }
        return null;
    }, [trip.days, trip.placeKey, trip.gather, nowSpot, nowMs, t]);

    /* 제때 도착하는지 — 목적지는 useNowSpot 이 낸 '다음 장소' 입니다.
       여기서 일정을 다시 시각순으로 훑으면 같은 판단이 두 벌이 됩니다.

       activeDay 가 오늘이 아닐 때는 묻지 않습니다. 지난 날이나 앞으로 올
       날을 보면서 "지금 가고 있나" 를 답할 수는 없습니다. */
    const liveTarget = useMemo(() => {
        if (activeDayStatus !== 'current') return null;
        const n = nowSpot.next;
        const lat = Number(n?.lat);
        const lng = Number(n?.lng);
        const hasCoord = Number.isFinite(lat) && Number.isFinite(lng) && !(!lat && !lng);
        /* 좌표를 몰라도 묻습니다 — 서버가 일정에서 다음 목적지를 찾습니다.
           예전에는 좌표가 없으면 null 을 내 아예 묻지 않았고, 일정 장소의
           절반이 좌표 없이 만들어져 있어(개발 DB 39곳 중 20곳) 그만큼
           카드가 조용히 사라졌습니다. */
        return {
            lat: hasCoord ? lat : undefined,
            lng: hasCoord ? lng : undefined,
            name: n?.name,
            /* 예정 시각은 그날 날짜에 장소의 시각을 얹어 만듭니다.
               이것도 없으면 서버가 일정에서 만들어 씁니다. */
            scheduledAt: scheduledIsoOf(activeDay?.date, n?.time),
        };
    }, [activeDayStatus, nowSpot, activeDay]);

    const { live: traceLive, trend: traceTrend } = useTraceLive(
        meetingId, liveTarget, !!liveTarget,
    );

    /* 발자취 — 마지막 날이 지나야 의미가 있습니다. 여행 중에 부르면
       아직 안 담긴 것을 보여 주게 되고(수집은 종료 3시간 뒤), 빈 섹션만 뜹니다. */
    const tripOver = useMemo(() => {
        if (trip.days.length === 0) return false;
        const last = trip.days[trip.days.length - 1];
        return computeDayStatus(last.date, nowMs) === 'past';
    }, [trip.days, nowMs]);

    /* 끝난 모임 — 마지막 일자가 지났거나 주최자가 종료한 것.
       일정을 손대는 버튼은 전부 감추고 모임 내용과 정산만 남깁니다. */
    const planLocked = tripOver || trip.isEnded;

    const { days: traceDays } = useMeetingTrace(meetingId, tripOver);
    /* 일정 다듬기 — 지난 여행의 발자취 집계. tripOver 로 막지 않습니다:
       여행 전·중에는 아직 고칠 수 있고, 끝난 뒤에는 회고가 됩니다.
       표본이 없으면 섹션이 스스로 숨습니다. */
    const { insights: traceInsights } = useTraceInsights(meetingId);

    /* 코스로 남기기 — 여행이 끝나고, 장소가 둘 이상일 때만.
       조건 판정(이미 남겼는지 등)은 서버가 합니다. 여기서 다시 세면 갈라집니다. */
    const [courseRegistered, setCourseRegistered] = useState(false);
    /* 정산을 마쳤는가 — 서버 값으로 시작하고, 마치면 바로 숨깁니다 */
    const [settled, setSettled] = useState(false);
    const coursePlaceNames = useMemo(
        () => trip.days.flatMap(d => d.places.map(p => p.name)).filter(Boolean),
        [trip.days],
    );
    const realtimeEnabled = detailOpen || activeDayStatus === 'current';
    const locations = useRealtimeMemberLocations(meetingId ?? null, trip.myUserNo, realtimeEnabled);

    const prevDays: DayView[] = useMemo(
        () => (trip.activeDayIdx > 0 ? trip.days.slice(0, trip.activeDayIdx) : []),
        [trip.days, trip.activeDayIdx],
    );
    const nextDays: DayView[] = useMemo(
        () => (trip.activeDayIdx < trip.days.length - 1 ? trip.days.slice(trip.activeDayIdx + 1) : []),
        [trip.days, trip.activeDayIdx],
    );

    // Pull-to-Refresh — 헤더 새로고침 버튼 대신
    const refreshAll = useCallback(async () => {
        if (!meetingId) return;
        await Promise.all([
            meetingStore.onGetMeetingDetail(meetingId),
            meetingStore.onGetMeetingMembers(meetingId),
            trip.reloadSubPlaces(),
        ]);
    }, [meetingId, trip.reloadSubPlaces]);
    const { containerRef: pullRef, pullDistance, isRefreshing: isPullRefreshing } = usePullToRefresh({
        onRefresh: refreshAll,
    });

    const handleEndMeeting = useCallback(async () => {
        if (!meetingId || isEnding) return;
        const ok = await showConfirm({
            title: t('meeting.endTitle'),
            message: t('meeting.endMsg'),
            confirmText: t('meeting.endBtn'),
        });
        if (!ok) return;
        setIsEnding(true);
        try {
            await MeetingRepository.onPostEndMeeting(meetingId);
            showAlert({ message: t('meeting.ended') });
            meetingStore.onGetMeetingDetail(meetingId);
        } catch (e) {
            showAlert({ message: t('meeting.endFail') });
        } finally {
            setIsEnding(false);
        }
    }, [meetingId, isEnding, showConfirm, showAlert, t]);

    const ladderParticipants: LadderParticipant[] = useMemo(
        () => trip.members.map((m) => ({ userNo: m.userNo, nickname: m.name })),
        [trip.members],
    );

    const handleShare = useCallback(async () => {
        if (!trip.inviteCode) {
            showAlert({ message: t('meeting.inviteCodeMissing') });
            return;
        }
        const link = `${window.location.origin}/invitation/${trip.inviteCode}`;
        const text = t('meeting.inviteText', { name: trip.name, code: trip.inviteCode });
        const success = await openShare({
            title: t('meeting.shareTitle', { name: trip.name }),
            text,
            url: link,
        });
        if (!success) {
            await copyToClipboard(`${text}\n${link}`);
            showAlert({ message: t('meeting.inviteLinkCopied') });
        }
    }, [trip.inviteCode, trip.name, showAlert, t]);

    return (
        <div className={cls("v6-page")} ref={pullRef}>
            <AppBar title={trip.title || t('meeting.title')} mode="sub" />

            <div className={cls("v6-ptr-indicator")} style={{ height: pullDistance > 0 ? pullDistance : 0 }}>
                <div
                    className={cls(`v6-ptr-spinner ${isPullRefreshing ? 'spinning' : 'pulling'}`)}
                    style={!isPullRefreshing ? { transform: `rotate(${pullDistance * 4}deg)` } : {}}
                />
            </div>

            <div className={cls("v6-scroll")}>
                {/* 0. 제목 — 어디를 언제 가는 여행인지.
                    .v6-scroll 은 좌우 여백이 없어 Bleed 없이도 화면 폭을 채우고,
                    사진이 없으면 먹색 면이 남아 모임마다 첫인상이 달라지지 않습니다. */}
                {trip.title ? (
                    <HeroBanner
                        imageUrl={heroImages[0]}
                        photos={heroImages}
                        tags={trip.isEnded ? [{ label: t('meetingFinished.endedBadge') }] : undefined}
                        title={trip.title}
                        paragraph={trip.destination?.name || undefined}
                        date={formatTripDuration(trip.startDate, trip.endDate, t) || undefined}
                    />
                ) : null}

                {/* 1. 여행 흐름 (TripStepper) */}
                <TripStepper
                    days={trip.days}
                    activeIdx={trip.activeDayIdx}
                    onSelect={trip.setActiveDay}
                    globalHotel={trip.globalHotel}
                    isPerDayHotel={trip.isPerDayHotel}
                    nowMs={nowMs}
                    onOpenMenu={openPlaceMenu}
                />

                {/* 2. 참여자 (여행 흐름 밑) */}
                {/* 마지막으로 누가 장소·시간을 바꿨는지. 변경이 없으면 그리지 않습니다 */}
                <LastChangeRow change={trip.lastChange} onOpen={() => setHistoryOpen(true)} />

                <ParticipantsStrip
                    members={trip.members}
                    onInvite={() => setInviteOpen(true)}
                    onDetail={() => setDetailOpen(true)}
                />

                {/* 내 참석 상태·위치 공유 — 바꿀 자리가 없어 상세에서 손을 댈 수
                    없었습니다. 백엔드와 저장소는 이미 있었고 화면만 없었습니다. */}
                <MyStatusRow me={me} busy={statusBusy} onChange={changeMyStatus} />

                {/* 3. 이전 일자 요약 목록 (활성 Day 위) */}
                {prevDays.map((d) => (
                    <DaySummaryCard key={d.date} day={d} onSelect={trip.setActiveDay} nowMs={nowMs} />
                ))}

                {/* 4. 활성 Day 상세 */}
                <ActiveDayPanel
                    day={activeDay}
                    /* 그날 묵는 숙소는 그 일자 패널에 보여줍니다.
                       예전에는 숙소가 여러 곳일 때만(isPerDayHotel) 띄워서, 한 곳만
                       등록하면 「숙박 등록」 버튼만 남고 등록한 숙소가 보이지 않았습니다.
                       위쪽 「전 일정 숙소」 는 여행 전체 요약이고, 이건 지금 보고 있는
                       날의 정보라 자리가 다릅니다. */
                    perDayHotel={activeDay ? activeDay.hotel : null}
                    destination={trip.destination}
                    members={trip.members}
                    locations={locations}
                    onAddPlace={handleAddPlace}
                    onAddHotel={handleAddHotel}
                    onSetGather={handleSetGather}
                    planLocked={planLocked}
                    nowSpot={nowSpot}
                    /* 미니지도를 누르면 화면을 바꾸지 않고 외부 지도앱을 고르게 합니다.
                       지금 보이는 자리(집결지·관광지·숙소)로 바로 길찾기하는 게 목적입니다.
                       일반 모임 상세도 같은 시트를 씁니다. */
                    onOpenMap={() => {
                        /* 우리 지도를 크게 봅니다. 참여자 목록이 쓰는 길과 같은 곳입니다. */
                        if (meetingId) navigate(`/meeting/location/${meetingId}`);
                    }}
                    onOpenDirections={() => {
                        if (nowSpot.kind === 'none') return;
                        setShowExternalMap(true);
                    }}
                    onOpenMenu={openPlaceMenu}
                    nowMs={nowMs}
                />

                {/* 4-0. 제때 도착할까요? — 지금 상황을 먼저 보고, 그다음에 남깁니다. */}
                {liveTarget && traceLive?.available && (
                    <div className={cls("v6-journal")}>
                        <TraceLiveCard
                            live={traceLive}
                            trend={traceTrend}
                            staying={traceTrend === 'stationary' || traceTrend === 'unknown'}
                            currentName={nowSpot.name}
                        />
                    </div>
                )}

                {/* 4-1. 기록 남기기 — 지금 어디인지 본 다음 남기는 순서가 자연스럽습니다.
                   앵커(장소·일차)는 nowSpot 이 이미 고른 것을 그대로 씁니다. */}
                {journalAnchor && meetingId && (
                    <div className={cls("v6-journal")}>
                        {/* 쓰는 자리와 읽는 자리를 함께 둡니다 — 이 카드가 작성기를
                            품고 있습니다. 예전에는 작성기만 붙여 놓아, 올린 사진과
                            글이 이 화면에서는 아예 보이지 않았습니다. */}
                        <JournalSummaryCard
                            meetingId={meetingId}
                            summary={journalSummary}
                            anchor={journalAnchor}
                            nickname={myNickname}
                            profileUrl={myProfileUrl}
                            onPosted={reloadJournalSummary}
                        />
                    </div>
                )}

                {/* 5. 다음 일자 요약 목록 (활성 Day 아래) */}
                {nextDays.map((d) => (
                    <DaySummaryCard key={d.date} day={d} onSelect={trip.setActiveDay} nowMs={nowMs} />
                ))}

                {/* 5-1. 우리 발자취 — 여행이 끝난 뒤에만 나타납니다.
                   담긴 게 없으면 섹션째 숨습니다(수집이 안 됐거나 위치 공유를 안 한 경우). */}
                {tripOver && traceDays.length > 0 && (
                    <div className={cls("v6-journal")}>
                        <TraceHistorySection days={traceDays} />
                    </div>
                )}

                {/* 5-1-1. 일정 다듬기 — 지난 여행의 발자취에서 찾은 숫자.
                   표본이 0이면 섹션째 숨습니다. 기획서 8.3 */}
                <div className={cls("v6-journal")}>
                    <TraceInsightSection insights={traceInsights} />
                </div>

                {/* 5-2. 코스로 남기기 — 여행이 끝난 뒤. 여행 모임은 종료 화면
                   (MeetingFinishedView)을 타지 않으므로 여기가 그 자리입니다. */}
                {tripOver && !courseRegistered && coursePlaceNames.length >= 2 && meetingId && (
                    <div className={cls("v6-journal")}>
                        <CourseRegisterSection
                            meetingId={meetingId}
                            regionName={trip.destination?.name}
                            days={trip.days.length}
                            placeNames={coursePlaceNames}
                            memberCount={trip.members.length}
                            thumbnailUrl={heroImages[0]}
                            onRegistered={() => setCourseRegistered(true)}
                        />
                    </div>
                )}

                {/* 5-2-1. 정산 마치기 — 여행 모임은 종료 화면을 타지 않아
                   정산 완료 버튼에 닿을 곳이 여기뿐입니다. 이걸 못 누르면
                   홈 「정산 대기 중」에서 영영 빠져나오지 못합니다. */}
                {tripOver && trip.isCreator && meetingId
                    && !trip.paymentSettledAt && !settled && (
                    <div className={cls("v6-journal")}>
                        <SectionHeader
                            title={t('meetingFinished.settleSectionTitle')}
                            sub={t('meetingFinished.settleSectionSub')}
                        />
                        <Button
                            variant="secondary"
                            block
                            onClick={async () => {
                                const ok = await showConfirm({
                                    title: t('meetingFinished.settleConfirmTitle'),
                                    message: t('meetingFinished.settleConfirmMsg'),
                                    confirmText: t('meetingFinished.settleConfirmBtn'),
                                });
                                if (!ok) return;
                                try {
                                    await MeetingRepository.onPostSettleMeeting(meetingId);
                                    setSettled(true);
                                    await showAlert({ message: t('meetingFinished.settleDoneMsg') });
                                } catch (e: any) {
                                    await showAlert({
                                        message: e?.response?.data?.message
                                            || t('meetingFinished.settleFail'),
                                    });
                                }
                            }}
                        >
                            {t('meetingFinished.settleAction')}
                        </Button>
                    </div>
                )}

                {/* 5-3. 사람들은 이렇게 다녀왔어요 — 여행 전·중에만.
                   여행 만들기 4단계에도 같은 레일이 있지만 거기서는 아직 모임이
                   없어서 "담기" 를 끝낼 수 없습니다(일정은 모임을 만든 뒤 이
                   화면에서 넣습니다). 담기가 실제로 되는 자리가 여기입니다.
                   이 지역 코스가 없으면 섹션째 숨습니다. */}
                {!tripOver && trip.travelRegionId && (
                    <div className={cls("v6-journal")}>
                        <CourseBrowseSection regionId={trip.travelRegionId} />
                    </div>
                )}

                {/* 6. 부가 */}
                <ExtrasStrip
                    onChat={() => navigate('/chat')}
                    onGame={() => setShowLadder(true)}
                    onExtend={() => navigate(`/meeting/edit/${meetingId}`)}
                    onSetGather={handleSetGather}
                    onEnd={handleEndMeeting}
                    canEnd={trip.isCreator && !trip.isRegular && !trip.isEnded}
                    /* 여행 모임은 날짜 범위로 굴러가 '종료시각 연장' 이 할 일이 없습니다.
                       그 자리는 집결지가 쓰는데, 끝난 모임이라 집결지를 감추면 연장이
                       대신 올라옵니다 — 원래 여행에는 없던 버튼이라 같이 막습니다. */
                    canExtend={trip.isCreator && !planLocked && trip.meetingType !== 'TRAVEL'}
                    showGather={trip.meetingType === 'TRAVEL' && !planLocked}
                />
            </div>

            <InviteSheet
                isOpen={inviteOpen}
                setIsOpen={setInviteOpen}
                meetingId={meetingId}
                inviteCode={trip.inviteCode}
                onShare={handleShare}
                onOpenInviteFromPast={() => setShowInviteFromPast(true)}
                externallyInvitedUserNos={invitedFromPastUserNos}
            />

            <ParticipantDetailSheet
                isOpen={detailOpen}
                setIsOpen={setDetailOpen}
                members={trip.members}
                locations={locations}
                destination={trip.destination}
                onOpenChat={() => navigate('/chat')}
                onOpenMap={() => meetingId && navigate(`/meeting/location/${meetingId}`)}
            />

            {/* 지금 보이는 자리로 길찾기 — 카카오맵·네이버지도·티맵(해외는 구글맵) */}
            <ExternalMapSheet
                visible={showExternalMap}
                placeName={nowSpot.name}
                address=""
                lat={nowSpot.lat}
                lng={nowSpot.lng}
                onClose={() => setShowExternalMap(false)}
            />

            <LadderGameModal
                open={showLadder}
                participants={ladderParticipants}
                onClose={() => setShowLadder(false)}
            />

            <PlaceMenuSheet
                isOpen={placeMenuOpen}
                setIsOpen={setPlaceMenuOpen}
                target={placeMenu}
                onPhotos={openPlacePhotos}
                onEdit={handleEditSubPlace}
                onDelete={handleDeleteSubPlace}
                planLocked={planLocked}
            />

            <ChangeHistorySheet
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                meetingId={meetingId}
            />

            <SubPlaceModal
                isOpen={showSubPlaceModal}
                onClose={handleCloseSubPlaceModal}
                meetingId={meetingId}
                editingSubPlace={editingSubPlace}
                isTravelMeeting={true}
                travelPeriod={trip.travelPeriod}
                subPlaces={trip.rawSubPlaces}
                onSaveSuccess={() => {
                    trip.reloadSubPlaces();
                    handleCloseSubPlaceModal();
                }}
                returnTo={meetingId ? `/meeting/detail/${meetingId}` : '/home'}
                initialForm={subPlaceForm}
                initialIsCustomVisitTime={isCustomVisitTime}
                travelDestinationName={trip.activeDayAnchor?.name || trip.destination?.name}
                travelDestinationCenter={
                    trip.activeDayAnchor
                        ? { lat: trip.activeDayAnchor.latitude, lng: trip.activeDayAnchor.longitude }
                        : trip.destination
                            ? { lat: trip.destination.latitude, lng: trip.destination.longitude }
                            : null
                }
                lockCategoryToHotel={subPlaceMode === 'hotel'}
                hideHotelInCategoryPicker={subPlaceMode === 'place'}
            />

            {meetingId ? (
                <InviteFromPastMeetings
                    open={showInviteFromPast}
                    onClose={() => setShowInviteFromPast(false)}
                    targetMeetingId={meetingId}
                    currentUserNo={trip.myUserNo || undefined}
                    excludeUserNos={trip.members.map((m) => m.userNo).filter(Boolean)}
                    onInviteSent={(sent) => {
                        setInvitedFromPastUserNos((prev) => Array.from(new Set([...prev, ...sent])));
                        meetingStore.onGetMeetingMembers(meetingId);
                    }}
                />
            ) : null}
        </div>
    );
};

export default MeetingDetailV6;
